import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { booksellerTtsService } from '../services/booksellerTtsService';
import type { TtsVoiceConfig, BooksellerSummary } from '../services/booksellerTtsService';
import Layout from '../components/Layout';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

/**
 * Bookseller TTS Configuration Page
 * Admin page for managing TTS voice availability for students
 * Protected by learnleaf.App.ManageBooksellerTts permission
 */
export const BooksellerTtsConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const [booksellers, setBooksellers] = useState<BooksellerSummary[]>([]);
  const [selectedBooksellerId, setSelectedBooksellerId] = useState<string | null>(null);
  const [voices, setVoices] = useState<TtsVoiceConfig[]>([]);
  const [isLoadingBooksellers, setIsLoadingBooksellers] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load booksellers on mount
  useEffect(() => {
    loadBooksellers();
  }, []);

  // Load configuration when bookseller is selected
  useEffect(() => {
    if (selectedBooksellerId) {
      loadConfiguration();
    }
  }, [selectedBooksellerId]);

  const loadBooksellers = async () => {
    try {
      setIsLoadingBooksellers(true);
      setError(null);

      const booksellersList = await booksellerTtsService.getAllBooksellers();
      setBooksellers(booksellersList);

      // Auto-select first bookseller if available
      if (booksellersList.length > 0) {
        setSelectedBooksellerId(booksellersList[0].id);
      }
    } catch (err: any) {
      console.error('Error loading booksellers:', err);
      setError(err?.response?.data?.error?.message || 'Failed to load booksellers');
    } finally {
      setIsLoadingBooksellers(false);
    }
  };

  const loadConfiguration = async () => {
    if (!selectedBooksellerId) return;

    try {
      setIsLoading(true);
      setError(null);

      const config = await booksellerTtsService.getConfiguration(selectedBooksellerId);
      setVoices(config.availableVoices);
    } catch (err: any) {
      console.error('Error loading TTS configuration:', err);
      setError(err?.response?.data?.error?.message || 'Failed to load TTS configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleVoice = (voiceId: string) => {
    setVoices((prev) =>
      prev.map((v) =>
        v.voiceId === voiceId
          ? { ...v, isEnabled: !v.isEnabled }
          : v
      )
    );
  };

  const handleSetDefault = (voiceId: string) => {
    setVoices((prev) =>
      prev.map((v) => ({
        ...v,
        isDefault: v.voiceId === voiceId,
        // Auto-enable if setting as default
        isEnabled: v.voiceId === voiceId ? true : v.isEnabled
      }))
    );
  };

  const handleSave = async () => {
    if (!selectedBooksellerId) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Validation: At least one voice must be enabled
      const enabledVoices = voices.filter((v) => v.isEnabled);
      if (enabledVoices.length === 0) {
        setError('At least one voice must be enabled');
        return;
      }

      // Validation: Exactly one voice must be default
      const defaultVoices = voices.filter((v) => v.isDefault);
      if (defaultVoices.length !== 1) {
        setError('Exactly one voice must be set as default');
        return;
      }

      // Validation: Default voice must be enabled
      const defaultVoice = voices.find((v) => v.isDefault);
      if (defaultVoice && !defaultVoice.isEnabled) {
        setError('Default voice must be enabled');
        return;
      }

      await booksellerTtsService.updateConfiguration(selectedBooksellerId, {
        availableVoices: voices
      });

      setSuccessMessage('TTS configuration saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving TTS configuration:', err);
      setError(err?.response?.data?.error?.message || 'Failed to save TTS configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const enabledCount = voices.filter((v) => v.isEnabled).length;
  const defaultVoice = voices.find((v) => v.isDefault);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            TTS Voice Configuration
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage which text-to-speech voices are available to students
          </p>
        </div>

        {/* Bookseller Selector */}
        {isLoadingBooksellers ? (
          <div className="mb-6 bg-white dark:bg-neutral-800 rounded-lg shadow-md p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-3 text-neutral-600 dark:text-neutral-400">Loading booksellers...</p>
          </div>
        ) : booksellers.length === 0 ? (
          <div className="mb-6 p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <p className="text-sm text-warning-800 dark:text-warning-200">
              No active booksellers found. Please create a bookseller first.
            </p>
          </div>
        ) : (
          <div className="mb-6 bg-white dark:bg-neutral-800 rounded-lg shadow-md p-6">
            <label htmlFor="bookseller-select" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Select Bookseller
            </label>
            <select
              id="bookseller-select"
              value={selectedBooksellerId || ''}
              onChange={(e) => setSelectedBooksellerId(e.target.value)}
              className="block w-full px-4 py-3 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              {booksellers.map((bookseller) => (
                <option key={bookseller.id} value={bookseller.id}>
                  {bookseller.displayName} ({bookseller.name})
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Configure TTS voices for the selected bookseller
            </p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg flex items-start gap-3">
            <XMarkIcon className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-error-800 dark:text-error-200 mb-1">Error</h3>
              <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg flex items-start gap-3">
            <CheckIcon className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-success-800 dark:text-success-200">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {!selectedBooksellerId ? (
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md p-8 text-center">
            <p className="text-neutral-600 dark:text-neutral-400">Please select a bookseller to configure TTS voices</p>
          </div>
        ) : isLoading ? (
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-neutral-600 dark:text-neutral-400">Loading voices...</p>
          </div>
        ) : (
          <>
            {/* Summary Card */}
            <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-info-900 dark:text-info-100 mb-2">
                    Current Configuration
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-info-800 dark:text-info-200">
                    <div>
                      <span className="font-medium">Enabled Voices:</span> {enabledCount} of {voices.length}
                    </div>
                    <div>
                      <span className="font-medium">Default Voice:</span>{' '}
                      {defaultVoice?.displayName || 'None'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Voices Table */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 dark:bg-neutral-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                        Voice
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider hidden md:table-cell">
                        Gender
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider hidden lg:table-cell">
                        Description
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                        Enabled
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                        Default
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                    {voices.map((voice) => (
                      <tr
                        key={voice.voiceId}
                        className={voice.isDefault ? 'bg-primary-50 dark:bg-primary-900/10' : ''}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                {voice.displayName}
                              </div>
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                {voice.voiceId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
                            {voice.gender}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400 hidden lg:table-cell max-w-xs truncate">
                          {voice.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleToggleVoice(voice.voiceId)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              voice.isEnabled
                                ? 'bg-primary-600'
                                : 'bg-neutral-200 dark:bg-neutral-700'
                            }`}
                            role="switch"
                            aria-checked={voice.isEnabled}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                voice.isEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleSetDefault(voice.voiceId)}
                            disabled={!voice.isEnabled && !voice.isDefault}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              voice.isDefault
                                ? 'border-primary-600 bg-primary-600'
                                : voice.isEnabled
                                ? 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400'
                                : 'border-neutral-200 dark:border-neutral-700 cursor-not-allowed opacity-50'
                            }`}
                          >
                            {voice.isDefault && (
                              <CheckIcon className="w-4 h-4 text-white" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile-only description cards (for voices with descriptions) */}
            <div className="lg:hidden mt-4 space-y-2">
              {voices
                .filter((v) => v.description)
                .map((voice) => (
                  <div
                    key={`desc-${voice.voiceId}`}
                    className="bg-neutral-50 dark:bg-neutral-700 rounded-lg p-3"
                  >
                    <div className="text-sm font-medium text-neutral-900 dark:text-white mb-1">
                      {voice.displayName}
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-300">
                      {voice.description}
                    </div>
                  </div>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={loadConfiguration}
                disabled={isLoading || isSaving}
                className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-200 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 bg-neutral-50 dark:bg-neutral-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-2">
                {t('App.Admin.TtsConfig.GuidelinesTitle')}
              </h3>
              <ul className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1 list-disc list-inside">
                <li>{t('App.Admin.TtsConfig.Guideline1')}</li>
                <li>{t('App.Admin.TtsConfig.Guideline2')}</li>
                <li>{t('App.Admin.TtsConfig.Guideline3')}</li>
                <li>{t('App.Admin.TtsConfig.Guideline4')}</li>
                <li>{t('App.Admin.TtsConfig.Guideline5')}</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
